import { storage } from '@newo/storage';
import { useCallback, useEffect, useState } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { runtimeConfig } from '@/constants';
import useAppStore, { CreatorChatFlowCallState } from '@/store/app';
import {
	CREATOR_CHAT_FLOW_CALL_STARTED_STORAGE_KEY,
	WEB_CALL_CONNECTOR_IDN,
	WEB_CALL_CUSTOMER_IDN,
	WEB_CALL_NEWO_CUSTOMER_IDN
} from '../constants';

type UseCreatorCallSessionOptions = {
	onCallStarted?: () => void;
};

export const useCreatorCallSession = ({
	onCallStarted
}: UseCreatorCallSessionOptions) => {
	const [audioLevel, setAudioLevel] = useState(0);
	const [hasStartedCallBefore, setHasStartedCallBefore] = useState(
		() =>
			storage.get<boolean>(CREATOR_CHAT_FLOW_CALL_STARTED_STORAGE_KEY) === true
	);
	const { actor, callState, setCreatorChatFlowCallState } = useAppStore(
		useShallow((state) => ({
			actor: state.actor,
			callState: state.creatorChatFlowCallState,
			setCreatorChatFlowCallState: state.setCreatorChatFlowCallState
		}))
	);
	const isCallStarted = callState === CreatorChatFlowCallState.Active;
	const isStartingCall = callState === CreatorChatFlowCallState.Starting;

	useEffect(() => {
		if (callState === CreatorChatFlowCallState.Idle) {
			setAudioLevel(0);
		}
	}, [callState]);

	useEffect(() => {
		return () => {
			setCreatorChatFlowCallState(CreatorChatFlowCallState.Idle);
		};
	}, [setCreatorChatFlowCallState]);

	const handleStartCall = useCallback(async () => {
		const widget = globalThis.WebCallWidget;
		if (
			!widget ||
			callState !== CreatorChatFlowCallState.Idle ||
			!actor?.external_id
		) {
			return;
		}

		const resetCallState = () => {
			setAudioLevel(0);
			setCreatorChatFlowCallState(CreatorChatFlowCallState.Idle);
		};

		try {
			let hasCallStarted = false;
			setCreatorChatFlowCallState(CreatorChatFlowCallState.Starting);
			await widget.startCall({
				apiBaseUrl: runtimeConfig.apiHostEndpoint,
				connectorIdn: WEB_CALL_CONNECTOR_IDN,
				customerIdn:
					runtimeConfig.partnerIdn === 'ionos'
						? WEB_CALL_CUSTOMER_IDN
						: WEB_CALL_NEWO_CUSTOMER_IDN,
				externalActorId: actor.external_id,
				onAudioLevelChange: (level) => {
					setAudioLevel(Math.min(1, Math.max(0, level)));
				},
				onCallStarted: () => {
					hasCallStarted = true;
					setHasStartedCallBefore(true);
					storage.set(CREATOR_CHAT_FLOW_CALL_STARTED_STORAGE_KEY, true);
					setCreatorChatFlowCallState(CreatorChatFlowCallState.Active);
					onCallStarted?.();
				}
			});

			if (!hasCallStarted) {
				resetCallState();
			}
		} catch {
			resetCallState();
		}
	}, [actor, callState, onCallStarted, setCreatorChatFlowCallState]);

	return {
		audioLevel,
		handleStartCall,
		hasStartedCallBefore,
		isCallStarted,
		isStartingCall
	};
};
